/**
 * The four sign-up consents (backend: services/consent/coreConsents.ts).
 *
 * The backend refuses a health write or an AI call with
 * 403 `CONSENT_REQUIRED` until the traveller has granted the current text.
 * Like a 402, the clients announce it and one listener - ConsentGate, mounted
 * in App - offers the consent in place, so a page that writes health data is
 * covered without knowing about consent at all.
 */

import apiClient from "@/services/api/apiClient";

export type CoreConsent =
  | "TERMS_OF_SERVICE"
  | "HEALTH_DATA"
  | "AI_PROCESSING"
  | "MARKETING_COMMUNICATIONS";

export type CoreDecision = "granted" | "denied" | "withdrawn" | "unasked";

export interface CoreConsentState {
  version: string;
  consents: Record<CoreConsent, { decision: CoreDecision; current: boolean }>;
  needsDecision: boolean;
}

export type CoreDecisions = Partial<Record<CoreConsent, boolean>>;

export const CORE_CONSENTS_QUERY = ["core-consents"] as const;

export async function getCoreConsents(): Promise<CoreConsentState> {
  const res = await apiClient.get("/consent/core");
  return res.data.data as CoreConsentState;
}

export async function saveCoreConsents(
  decisions: CoreDecisions,
): Promise<CoreConsentState> {
  const res = await apiClient.put("/consent/core", decisions);
  return res.data.data as CoreConsentState;
}

/** Granted to the text currently in force. */
export function isGranted(
  state: CoreConsentState | undefined,
  type: CoreConsent,
): boolean {
  const entry = state?.consents[type];
  return entry?.decision === "granted" && entry.current;
}
